<template>
  <div class="email">
    <a-modal
      :open="open"
      :wrapClassName="displayModalClass"
      @cancel="handleConfirmClose"
      destroyOnClose
      :maskClosable="false"
      width="100%"
      :footer="null"
      :mask="displayMask"
    >
      <div class="general" v-if="!(status === 'minimized')">
        <div class="header">
          <h3>
            Redactar nuevo correo 
            <span class="draft-badge" v-if="showDraft">(BORRADOR)</span>
          </h3>
          <div class="actions">
            <LineOutlined @click="minimizeModal" />
            <ExpandAltOutlined
              @click="expandModal"
              v-if="!(status === 'expanded') && !replyEmail"
            />
            <CloseOutlined @click="handleConfirmClose" />
          </div>
        </div>
        <!-- <RedactEmailForm
          ref="redactFormRef"
          :defaultEmail="props.defaultEmail"
          :emailTransmitter="props.emailTransmitter"
          @handleChangeEmailFrom="$emit('handleChangeEmailFrom')"
        /> -->
        <RedactEmailForm
          ref="redactFormRef"
          :isReply="isReply"
          :open="open"
          :emailTransmitter="props.emailTransmitter"
          @handleChangeEmailFrom="$emit('handleChangeEmailFrom')"
        />
        <!-- <footer> -->
        <div class="footer">
          <a-button @click="handleConfirmClose">Cancelar</a-button>
          <a-button type="primary" :loading="sending" @click="handleOk">Enviar</a-button>
        </div>
        <!-- </footer> -->
      </div>
      <MinimizedEmail
        v-if="status === 'minimized'"
        :showDraft="showDraft"
        :status="status"
        @emitExpandEvent="handleStatus"
        @emitCloseEvent="handleConfirmClose"
        :open="open"
      />
    </a-modal>
  </div>
</template>
<script lang="ts" setup>
import { ref, watch, inject, computed, Ref, nextTick, onMounted, createVNode } from "vue";
import { CloseOutlined, ExpandAltOutlined, LineOutlined, QuestionCircleOutlined } from "@ant-design/icons-vue";
import MinimizedEmail from "./status/MinimizedEmail.vue";
import RedactEmailForm from "@/app/emails/components/forms/RedactEmailForm.vue";
import { message, Modal } from "ant-design-vue";
import { emailService } from "@/app/emails/services/Email.service";
import { SendEmailDto } from "@/@types/emails/dtos";
import { DRAFT_KEY, EmailForm, IMenutem } from "@/@types/emails";
import { ResponseError } from "@/app/shared/utils/httpManager";

import { useStore } from "vuex";

import HslToHex from "@/app/shared/utils/hslToHex";
// import InputSection from "@/app/workspace/components/chat/input/InputSection.vue";

const props = defineProps<{
  open: boolean;
  isReply: { type: boolean; default: false };
  emailTransmitter: { type: boolean; default: false };
  // defaultEmail: {
  //   type: EmailForm;
  //   default: {
  //     fromEmail: null;
  //     selectedItems: [];
  //     selectedItemsCC: [];
  //     selectedItemsCCO: [];
  //     subject: "";
  //     text: "";
  //   };
  // };
}>();

const status = ref("normal");
const replyEmail = inject("replyEmail") as Ref<boolean>;
const redactFormRef = ref<InstanceType<typeof RedactEmailForm>>();
const hslToHex = new HslToHex();

const displayModalClass = computed(() => {
  if (status.value === "normal") {
    return replyEmail.value ? "full-modal" : "email__modal";
  } else if (status.value === "minimized") {
    handleShowDraft();
    return "email__modal";
  } else {
    return "full-modal";
  }
});

const displayMask = computed(() => {
  if (status.value === "expanded" || displayModalClass.value === "full-modal") return true;
  return false;
});

// const onClose = (removedTag) => {
//   const newSelectedItems = selectedItems.value.filter((item) => item !== removedTag);
//   selectedItems.value = newSelectedItems;
// };

const emit = defineEmits<{
  "update:open": [open: boolean];
  updateList: [{ fromEmailAddress: string }];
  handleChangeEmailFrom: [];
  close: [defaultEmail: EmailForm];
}>();

const sending = ref(false);

const getAllDrafts = () => {
  try {
    return JSON.parse(localStorage.getItem(DRAFT_KEY) || '{}') || {};
  } catch {
    return {};
  }
};

const store = useStore();

const usedIdLS = () => {
  return localStorage.getItem('used_id') || '';
};

const getUserDraft = () => {
  const drafts = getAllDrafts();
  return usedIdLS() ? drafts?.[usedIdLS()] : null;
};

const clearDraft = () => {
  const drafts = getAllDrafts();
  delete drafts[usedIdLS()];
  localStorage.setItem(DRAFT_KEY, JSON.stringify(drafts));
  store.dispatch('clearDraftFiles');
};

const handleOk = async () => {
  try {
    const form = redactFormRef.value?.formState;
    if (!form) throw new Error("No email data");

    if (!form.fromEmail) {
      message.error("Selecciona un remitente para tu correo.");
      return;
    }

    if (!form.selectedItems || form.selectedItems.length === 0) {
      message.error("Ingresa al menos un destinatario para tu correo.");
      return;
    }

    // Limpiamos los tags HTML para verificar si hay texto real en el editor.
    const rawText = (form.text || "").replace(/<[^>]*>/g, "").trim();
    if (!rawText) {
      message.error("El mensaje de correo es requerido");
      return;
    }

    sending.value = true;
    const files = redactFormRef.value?.files;
    const convertedHtml = hslToHex.convertHslToHex(form.text);
    const body: SendEmailDto = {
      channel_customer: form.fromEmail,
      to_emails: form.selectedItems.join(","),
      message: convertedHtml,
      subject: form.subject,
      cc_emails: form.selectedItemsCC.join(","),
      bcc_emails: form.selectedItemsCCO.join(","),
      attachments: files,
      send_as_alias: form.sendAsAlias || null,
    };
    await emailService.sendEmail(body);

    emit("update:open", false);
    emit("updateList", { fromEmailAddress: redactFormRef.value?.fromEmailAddress });
    status.value = "normal";
    clearDraft();
    message.success("Se ha enviado el correo con éxito");
  } catch (error: any) {
    console.error(error);
    let errorMessage = "Ha ocurrido un error";
    if (error instanceof ResponseError) errorMessage = error.getErrorMessage();
    message.error(errorMessage);
  } finally {
    sending.value = false;
  }
};

const handleClose = () => {
  if (!props.isReply) clearDraft();
  emit("close", redactFormRef.value?.formState);
  nextTick(() => {
    status.value = "normal";
    replyEmail.value = false;
    emit("update:open", false);
  });
};

const handleConfirmClose = () => {
  console.log('entra aca', props.isReply, getUserDraft());
  if (!props.isReply) {
    if (getUserDraft()) {
      Modal.confirm({
        title: "¿Deseas descartar los cambios realizados?",
        content: "Recuerda que si no envias los cambios registrados, estos se perderán.",
        icon: createVNode(QuestionCircleOutlined),
        onOk: () => {
          handleClose()
          message.success("Borrador de correo eliminado con éxito");
        },
        centered: true,
        okText: "Sí, descartar cambios",
        cancelText: "No, cancelar",
      });
      return;
    }
  }
  
  handleClose();
};
const minimizeModal = () => {
  status.value = "minimized";
};
const expandModal = () => {
  status.value = "expanded";
};
const handleStatus = (message: string) => {
  status.value = message;
};
const handleVisibility = (message: boolean) => {
  emit("update:open", message);
  status.value = "normal";
};

defineExpose({ redactFormRef: redactFormRef.value, minimizeModal, handleStatus });

const menuItemsLoaded = computed(() => {
  return store.getters.getMenuItemsLoaded;
});

const menuItems = computed(() => {
  return store.getters.getMenuItems;
});

watch(
  () => [menuItems.value, menuItemsLoaded.value],
  ([items, loaded]) => {
    const draft = getUserDraft();

    if (!loaded || !draft) return;

    const identifiers = items.flatMap(
      (i: IMenutem) => i.subItems
    );

    if (!draft?.fromEmail) return;

    const draftExistsInMenu = identifiers?.some((el: any) => Number(el.id) == Number(draft?.fromEmail));

    if (draft && !draftExistsInMenu) {
      handleClose();
    }
  },
  { immediate: true }
);

const showDraft = ref(false);

const handleShowDraft = () => {
  const draft = getUserDraft();
  showDraft.value = Boolean(draft);
};

watch(
  () => props.open,
  (val) => {
    if (val) handleShowDraft();
  },
  { immediate: true }
);

onMounted(() => {
  const draft = getUserDraft();
  console.log(draft, "draft");

  if (draft && !props.isReply) {
    status.value = "minimized";
  }
})
</script>
<style lang="sass" scoped>
p
  margin: 0
.email__modal
  .ck-content
    height: 300px
.full-modal
  .ant-form-item
    margin-bottom: 7px
.header
  display: flex
  justify-content: space-between
  padding-bottom: 12px
  .actions
    display: flex
    gap: 15px
footer
  display: flex
  justify-content: flex-end
  gap: 12px
  padding: 10px 10px 0
.flex-container
  display: flex
  justify-content: space-between
  align-items: center

.draft-badge
  color: #00000073

:global(.ant-modal-wrap.email__modal)
  pointer-events: none !important

.general
  display: flex
  flex-direction: column
  height: 100%
  overflow: hidden

.footer
  margin-top: 10px
  display: flex
  gap: 10px
  flex-shrink: 0 
</style>
<style>
.email__modal {
  width: 100%;
  max-height: 100%;
  position: relative;
}
.email__modal .ant-modal {
  width: 100%;
  min-height: 73vh;
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
}

.email__modal .ant-modal-content {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 630px;
  /* height: 660px; */
  display: flex;          
  flex-direction: column; 
  overflow: hidden;       
}
.email__modal .ck-content {
  min-height: 300px;
}
.reply .ck-content {
  min-height: 200px;
}
.full-modal .ant-modal {
  position: absolute;
  top: 52%;
  left: 50%;
  transform: translate(-50%, -50%);
}
.full-modal .ck-content {
  min-height: 300px;
}
.email__modal .ant-modal-close,
.full-modal .ant-modal-close {
  display: none;
}

.email__modal .ant-form-item .ant-form-item-label,
.full-modal .ant-form-item .ant-form-item-label,
.reply .ant-form-item .ant-form-item-label {
  width: 55px;
  text-align: left;
}
.email__modal .ant-select-multiple .ant-select-selection-overflow {
  max-width: 94%;
}
.email__modal .ck-editor__editable {
  overflow-y: auto !important;
  max-height: 280px;
}
</style>
